// Does a ticket the server sent still belong in the Dashboard view it was
// fetched for?
//
// The Dashboard list comes from the server (search_tickets_v2: tab, chips,
// counts and paging are all done in SQL, so no ticket can fall off a page cap).
// But the screen must still react the instant an agent clicks Resolve / Claim /
// Snooze, before the server has answered: the optimistic patch changes the row
// in the cache, and this check takes it out of a tab it no longer belongs to —
// exactly what the old client-side list did. It also hides the previous tab's
// rows while a tab switch is still loading.
//
// The rules are the ones the SQL already applied, so on an unchanged row this
// returns true. Only fields the optimistic patch can change matter in practice.
//
// Pure on purpose (no React, no Supabase): tests/dashboard-view.test.ts.

import { matchesGroup, type GroupKey, type GroupableTicket } from './ticket-groups';

export interface ViewTicket extends GroupableTicket {
  priority?: string | null;
  category?: string | null;
  is_existing_client?: boolean | null;
  /** Unread for me (migration 081). Missing = the list never said. */
  unread?: boolean | null;
}

export interface DashboardView {
  group: GroupKey;
  mySlackId: string | null;
  /** The tickets I watch. `null` while that list is still loading: the server
   *  already applied "Watching", so don't second-guess membership yet. */
  watchedIds: ReadonlySet<string> | null;
  priorities?: readonly string[];
  statuses?: readonly string[];
  categories?: readonly string[];
  isExistingClient?: boolean;
  assigneeSlackIds?: readonly string[];
  /** The Unread chip is on. */
  unread?: boolean;
}

export function stillInView(ticket: ViewTicket, view: DashboardView, now: number = Date.now()): boolean {
  const watched = view.watchedIds ?? new Set([ticket.id]);
  if (!matchesGroup(ticket, view.group, view.mySlackId, watched, now)) return false;
  if (view.priorities?.length && !view.priorities.includes(ticket.priority ?? '')) return false;
  if (view.statuses?.length && !view.statuses.includes(ticket.status ?? '')) return false;
  if (view.categories?.length && !view.categories.includes(ticket.category ?? '')) return false;
  if (typeof view.isExistingClient === 'boolean' && (ticket.is_existing_client ?? false) !== view.isExistingClient) {
    return false;
  }
  // With the Unread chip on, a ticket you just opened (unread now false) closes up.
  // A row that never carried the flag is left to the server.
  if (view.unread && ticket.unread === false) return false;
  if (view.assigneeSlackIds?.length) {
    if (!ticket.assignee_slack_id || !view.assigneeSlackIds.includes(ticket.assignee_slack_id)) return false;
  }
  return true;
}
